import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import Spinner from "../Components/Spinner";
import axios from "axios";
import { useAuth } from "../hooks/AuthProvider";
import { BASE_URL_API, STATIC_ICONS } from "../Constants";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddDashboard } from "./AddDashboard";
import { TransferDashboard } from "./DashboardDetails";
import { useNavigate } from "react-router-dom";
import "../Components/modal.css";
import "./edit.css";
import { deleteWithIcon, deleteMultiWithIcon } from "../lib/actions";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TranferIcon from "@mui/icons-material/SyncAltOutlined";
import { TemplateIcon } from "../Icons";
import { LanguageContext } from "../context/LanguageContext";
import dashTranslations from "../languages/dashboard.json";
import { getErrorMessage } from "../utils/errorUtils";
import { useFontSize } from "../context/FontSizeContext";
import AppDialog from "../Components/AppDialog"; 
import { normalizeDashboardListRow } from "../utils/listRowIds";

export const Dashboards = () => {
  const { lang } = useContext(LanguageContext);
  const { selectedSize } = useFontSize();
  const t = dashTranslations[lang];
  const isRtl = ["ar", "ur"].includes(lang);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [transfer, setTransfer] = useState({ open: false, id: 0 });

  const { permissions, org } = useAuth();
  const navigate = useNavigate();
  const canCreateDashboard = permissions["add_new_dashoard"];
  const pagingoptions = [10, 20, 50, 100];

  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "alert", // "alert" | "confirm"
    variant: "info", // "info" | "danger" | "error"
    icon: "alert", // "alert" | "delete"
    title: "",
    message: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || "alert",
          variant: opts?.variant || "info",
          icon: opts?.icon || "alert",
          title: opts?.title || "",
          message: opts?.message || "",
          confirmText: opts?.confirmText || "Ok",
          cancelText: opts?.cancelText || "Cancel",
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    []
  );

  const showAlert = useCallback(
    async (type, message) => {
      const variant =
        type === "error" ? "error" : type === "warning" ? "info" : "info";

      await alert({
        type: "alert",
        variant,
        icon: "alert",
        title:
          type === "error"
            ? t?.alert?.errorTitle || "Error"
            : type === "warning"
            ? t?.alert?.warningTitle || "Warning"
            : t?.alert?.infoTitle || "Info",
        message: message || "",
        confirmText: "Ok",
      });
    },
    [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle]
  );

  const baseColumns = [
    {
      field: "icon",
      headerName: "",
      width: 60,
      align: "center",
      renderCell: (params) =>
        params.row.icon ? (
          <img
            src={STATIC_ICONS + params.row.icon}
            alt=""
            style={{
              width: "32px",
              height: "32px",
              marginTop: "8px",
              alignItems: "center",
            }}
          />
        ) : (
          <TemplateIcon color={"var(--accent)"} />
        ),
    },
    {
      field: "name",
      headerName: t.column.name,
      width: 400,
      align: "left",
      headerAlign: "left",
      headerClassName: "datagrid-header",
      cellClassName: "datagrid-cell",
    },
    {
      field: "type",
      headerName: t.column.type || "Type",
      width: 190,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "organization_name",
      headerName: t.column.organization,
      width: 400,
      align: "left",
      headerAlign: "left",
      headerClassName: "datagrid-header",
      cellClassName: "datagrid-cell",
    },
  ];

  const canDeleteDashboards = permissions["delete_dashboards"];
  const canTransferDashboards = permissions["edit_dashboard"];
  const canShowActionsColumn = canDeleteDashboards || canTransferDashboards;

  const columns = canShowActionsColumn
    ? [
        {
          field: "actions",
          headerName: t.column.actions,
          type: "actions",
          width: 100,
          getActions: (params) =>
            [
              canTransferDashboards ? (
                <GridActionsCellItem
                  icon={<TranferIcon />}
                  label={t?.transferDashboard?.title || "Transfer Dashboard"}
                  onClick={() => setTransfer({ open: true, id: params.row.id })}
                  showInMenu
                />
              ) : null,
              canDeleteDashboards ? (
                <GridActionsCellItem
                  icon={<DeleteIcon style={{ color: "red" }} />}
                  label={t.delete}
                  onClick={() =>
                    deleteWithIcon(
                      params.row.id,
                      params.row.icon,
                      "dashboard",
                      fetchDashboards,
                      alert
                    )
                  }
                  showInMenu
                />
              ) : null,
            ].filter(Boolean),
        },
        ...baseColumns,
      ]
    : baseColumns;

  const fetchDashboards = useCallback(async () => {
    if (!org?.id) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL_API}/dashboard/filtered/${org.id}`);
      if (res.data && Array.isArray(res.data.data)) {
        setRows(res.data.data.map(normalizeDashboardListRow));
      } else {
        console.error("Received unexpected data format:", res.data);
        setRows([]);
        await showAlert("error", "Received an invalid format from the server.");
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      await showAlert("error", errorMessage);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [org?.id, showAlert]);

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  const [selectedRows, setSelectedRows] = useState([]);

  const handleMultiDelete = async (ids) => {
    if (!canDeleteDashboards) {
      await showAlert("error", "You do not have permission to delete dashboards.");
      return;
    }
    const icons = ids.map((id) => rows.find((row) => row.id === id)?.icon);
    await deleteMultiWithIcon(ids, icons, "dashboard", fetchDashboards, alert);
    setSelectedRows([]);
  };

  const selectActions = canDeleteDashboards
    ? [
        {
          title: "Delete",
          icon: (
            <DeleteIcon
              style={{
                color: "rgb(236, 69, 78)",
                marginInlineEnd: "20px",
              }}
            />
          ),
          action: handleMultiDelete,
        },
      ]
    : [];

  const colors = {
    headerBackground: "var(--header-background)",
    headerColor: "var(--header-color)",
    headerHover: "var(--header-hover)",
    cellBackground: "var(--cell-background)",
    cellColor: "var(--cell-color)",
    cellHover: "var(--cell-hover)",
    orderColor: "var(--order-color)",
    orderSelectedColor: "rgb(49, 199, 149)",
    checkboxSelection: "var(--accent)",
    btnBackground: "rgb(219, 245, 236)",
    btnColor: "var(--accent)",
    modalBorder: "var(--modal-border)",
    red: "rgb(236, 69, 78)",
  };

  const handleRowClick = (params) => navigate("/dashboards/details/" + params.row.id);

  return permissions["view_dashboards"] ? (
    <div
      className="configuration devices-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
      }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {openAddModal && canCreateDashboard && (
        <AddDashboard
          bg={colors.cellBackground}
          fg={colors.cellColor}
          ft={colors.headerBackground}
          btnPrimary={colors.btnBackground}
          btnSecondary={colors.btnColor}
          textColor={colors.headerColor}
          open={openAddModal}
          setOpen={setOpenAddModal}
          refresh={fetchDashboards}
        />
      )}
      {transfer.open && (
        <TransferDashboard
          state={transfer}
          setState={setTransfer}
          refresh={fetchDashboards}
        />
      )}

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{t.dashboardsPageTitle}</h2>
          </div>
          {canCreateDashboard ? (
            <button className="devices-primary-btn" onClick={() => setOpenAddModal(true)}>
              <span>{t.newDashboard}</span>
            </button>
          ) : null}
        </div>

        {loading ? (
          <Spinner />
        ) : rows.length > 0 ? (
          <div
            className="devices-grid-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
              marginTop: "20px",
            }}
          >
            <DataGridTable
              columns={columns}
              rows={rows}
              pagingoptions={pagingoptions}
              idcolumn={"id"}
              handleRowClick={handleRowClick}
              checkboxSelection={canDeleteDashboards}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="dashboards"
              rowStyle={{
                fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.noData}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div style={{ fontSize: "calc(var(--body-font-size) * var(--font-size-multiplier))" }}>
      {t.noPermission}
    </div>
  );
};
